import { HStack, Text, VStack } from '@ayble-health/dashboard-components';
import { Box, useToken } from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { formatDate } from '../utils';
import { useElimination } from 'api/healthCoach/useElimination';
import { Loader } from 'components/Loader';

const formatReadyForFoodChallenge = (
  readyForFoodChallenge: boolean | null
): string => {
  if (readyForFoodChallenge === true) return 'Yes';
  if (readyForFoodChallenge === false) return 'No';
  return '--';
};

type Props = {
  blockId?: string;
};

export const EliminationDetailsContent = ({ blockId }: Props) => {
  const { elimination, isLoading } = useElimination({
    eliminationOrUserBlockId: blockId,
  });
  const formattedEndedOn = formatDate(
    elimination?.ended_on ?? undefined,
    'MMM D'
  );
  const [secondary200] = useToken('colors', ['secondary.200']);

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <VStack>
      <Box
        bgColor={'feather.100'}
        borderColor={'cloud.200'}
        borderRadius={'xl'}
        borderWidth={1}
        overflow={'hidden'}
      >
        <HStack
          alignItems={'center'}
          borderBottomWidth={1}
          borderColor={'cloud.200'}
          justifyContent={'space-between'}
          px={4}
          py={3}
        >
          <Text color={'secondary.500'} fontSize={'md'} fontWeight={'medium'}>
            App Check-in
          </Text>
          <Text color={'secondary.200'} fontSize={'sm'} fontWeight={'medium'}>
            {formattedEndedOn}
          </Text>
        </HStack>

        <VStack px={4} py={4} gap={4}>
          <HStack alignItems={'flex-start'} gap={2}>
            <Check color={secondary200} size={12} style={{ marginTop: 4 }} />

            <Text
              color={'secondary.500'}
              flex={1}
              fontSize={'md'}
              fontWeight={'normal'}
            >
              Current step:{' '}
              <Text color={'secondary.500'} fontSize={'md'} fontWeight={'bold'}>
                {elimination?.step || '--'}
              </Text>
            </Text>
          </HStack>

          {!elimination?.checkins.length && (
            <Text color={'blueGray.600'} fontSize={'sm'}>
              No check-ins available.
            </Text>
          )}

          {elimination?.checkins.map((checkIn) => (
            <VStack key={checkIn.id} gap={1}>
              <Text
                color={'secondary.500'}
                fontSize={'md'}
                fontWeight={'normal'}
              >
                Check-in type:{' '}
                <Text
                  color={'secondary.500'}
                  fontSize={'md'}
                  fontWeight={'bold'}
                >
                  {checkIn.checkin_type || '--'}
                </Text>
              </Text>

              <Text
                color={'secondary.500'}
                fontSize={'md'}
                fontWeight={'normal'}
              >
                Date:{' '}
                <Text
                  color={'secondary.500'}
                  fontSize={'md'}
                  fontWeight={'bold'}
                >
                  {formatDate(checkIn.ended_on ?? undefined, 'MMM D')}
                </Text>
              </Text>

              <Text
                color={'secondary.500'}
                fontSize={'md'}
                fontWeight={'normal'}
              >
                User sentiment:{' '}
                <Text
                  color={'secondary.500'}
                  fontSize={'md'}
                  fontWeight={'bold'}
                >
                  {checkIn.user_sentiment || '--'}
                </Text>
              </Text>

              <Text
                color={'secondary.500'}
                fontSize={'md'}
                fontWeight={'normal'}
              >
                Ready for food challenge:{' '}
                <Text
                  color={'secondary.500'}
                  fontSize={'md'}
                  fontWeight={'bold'}
                >
                  {formatReadyForFoodChallenge(
                    checkIn.ready_for_food_challenge
                  )}
                </Text>
              </Text>

              <Text
                color={'secondary.500'}
                fontSize={'md'}
                fontWeight={'normal'}
              >
                {checkIn.open_feedback ? `"${checkIn.open_feedback}"` : '--'}
              </Text>
            </VStack>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};
